import * as MutationsType from "./mutationsType";
import { contactsCollection } from "@/config/firebase";
import requestAxios from "@/helpers/request_axios";
import { roles } from "@/helpers/get_constants";

export default {
  async getContactById({ commit, state }, { id }) {
    if (state.unsubscribeGetContact) {
      state.unsubscribeGetContact();
      commit(MutationsType.SET_UNSUBSCRIBE_GET_CONTACT, null);
    }

    if (!id) {
      commit(MutationsType.SET_CONTACT, null);
      return null;
    }

    commit(MutationsType.SET_CONTACT, null);

    return await new Promise((resolve, reject) => {
      let firstSnapshot = true;
      const unsubscribe = contactsCollection.doc(id).onSnapshot(
        (doc) => {
          if (!doc.exists) {
            const error = new Error("El contacto solicitado no existe");
            if (firstSnapshot) reject(error);
            firstSnapshot = false;
            return;
          }

          const contact = { ...doc.data(), id: doc.id };
          commit(MutationsType.SET_CONTACT, contact);
          if (firstSnapshot) resolve(contact);
          firstSnapshot = false;
        },
        (error) => {
          if (firstSnapshot) reject(error);
          else console.error("getContactById listener: ", error);
          firstSnapshot = false;
        },
      );

      commit(MutationsType.SET_UNSUBSCRIBE_GET_CONTACT, unsubscribe);
    });
  },
  stopContactListener({ commit, state }) {
    if (state.unsubscribeGetContact) state.unsubscribeGetContact();
    commit(MutationsType.SET_UNSUBSCRIBE_GET_CONTACT, null);
    commit(MutationsType.SET_CONTACT, null);
  },
  // eslint-disable-next-line
  async getContactByDocumentNumber({}, { documentNumber }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/contact/find",
        method: "GET",
        params: {
          document_number: documentNumber,
        },
      });
      return response.data.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async getAllContacts({ commit, rootState }) {
    let agent = rootState.Agent.agent;
    let reference = contactsCollection;

    if ([roles.ADVISOR, roles.SUPERVISOR].includes(agent.role))
      reference = reference.where("agent_id", "==", agent.uid);
    if ([roles.ENTREPRENEUR].includes(agent.role)) reference = reference.where("team_id", "==", agent.team_id);

    await reference.orderBy("created_at", "desc").onSnapshot(async (snap) => {
      let data;
      commit(MutationsType.SET_CONTACTS, []);
      snap.docs.forEach((doc) => {
        data = doc.data();
        commit(MutationsType.ADD_CONTACT, { id: doc.id, ...data });
      });
    });
  },
  async getAllNewContacts({ commit, rootState }, payload) {
    try {
      let agent = rootState.Agent.agent;
      let reference = contactsCollection;

      if ([roles.ADVISOR, roles.SUPERVISOR].includes(agent.role))
        reference = reference.where("agent_id", "==", agent.uid);
      if ([roles.ENTREPRENEUR].includes(agent.role)) reference = reference.where("team_id", "==", agent.team_id);

      reference = reference.orderBy("created_at", "asc");

      if (payload.last_contact) {
        const start = await contactsCollection.doc(payload.last_contact.id).get();
        reference = reference.startAfter(start);
      }

      await reference.onSnapshot(async (snap) => {
        let newContacts = [];

        snap.docs.forEach((doc) => {
          newContacts.push({ id: doc.id, ...doc.data() });
        });

        commit(MutationsType.SET_NEW_CONTACTS, newContacts);
      });
    } catch (error) {
      console.error("getAllNewContacts: ", error);
    }
  },
  async setGroupContacts({ commit }, payload) {
    commit(MutationsType.SET_CONTACTS, payload.contacts_selected);
  },
  // eslint-disable-next-line
  async createContact({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/contact",
        method: "POST",
        data: {
          origin: "agent",
          ...payload.contact,
        },
      });
      return response.data;
    } catch (error) {
      console.error("createContact: ", error);
      return error.response.data;
    }
  },
  // eslint-disable-next-line
  async setContactAgent({ commit }, payload) {
    try {
      await contactsCollection.doc(payload.contact_id).update({
        agent_id: payload.agent.id,
        agent: {
          uid: payload.agent.id,
          full_name: payload.agent.full_name,
          email: payload.agent.email,
        },
      });
    } catch (error) {
      console.error("setContactAgent: ", error);
    }
  },
  // eslint-disable-next-line
  async setContactTerms({ commit }, payload) {
    try {
      return await requestAxios({
        url: "/awy/api/v1/contact/set-terms",
        method: "POST",
        data: payload,
      });
    } catch (error) {
      console.error("setContactTerms: ", error);
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async updateContact({ commit }, { id, data }) {
    try {
      let response = await requestAxios({
        url: `/awy/api/v1/contact/${id}`,
        method: "PATCH",
        data: data,
      });
      commit(MutationsType.UPDATE_CONTACT, response.data.data);
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async searchContacts({}, { filters, search, page, perPage = 40 }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/contact/search",
        method: "POST",
        data: {
          filters,
          search,
          page,
          perPage,
        },
      });
      return response.data;
    } catch (error) {
      console.error("searchContacts: ", error);
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async downloadContacts({ commit }, payload) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/contact/download",
        method: "GET",
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("downloadContacts: ", error);
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async exportContactTemplate({ commit }) {
    try {
      let response = await requestAxios({
        url: "/awy/api/v1/contact/export-template",
        method: "GET",
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("exportContactTemplate: ", error);
      throw error.response.data;
    }
  },
  // eslint-disable-next-line
  async importContacts({ commit }, payload) {
    try {
      let formdata = new FormData();
      formdata.append("file", payload.file);
      let response = await requestAxios({
        url: "/awy/api/v1/contact/import",
        method: "POST",
        data: formdata,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("importContacts: ", error);
      throw error.response.data;
    }
  },
  resetModuleContact({ commit }) {
    commit(MutationsType.SET_CONTACT, null);
    commit(MutationsType.SET_CONTACTS, null);
    commit(MutationsType.SET_NEW_CONTACTS, null);
  },
};
